/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CronProfileForm.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Clock, Info, Calendar } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';

// Layout Components
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FixedActionBar } from '../../components/ui/FixedActionBar';


// UI Components
import { FormBox } from '../../components/ui/FormBox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Toggle from '../../components/ui/Toggle';
import Select from '../../components/ui/select';

// API & Types
import { createCronProfile, updateCronProfile, getCronProfile } from './api';
import { CronProfileCreatePayload } from './api';

const CRON_TEMPLATES = [
    { id: 'custom', labelKey: 'cronProfiles.templates.custom' },
    { id: '* * * * *', labelKey: 'cronProfiles.templates.everyMinute' },
    { id: '0 * * * *', labelKey: 'cronProfiles.templates.hourly' },
    { id: '0 0 * * *', labelKey: 'cronProfiles.templates.daily' },
    { id: '0 0 * * 0', labelKey: 'cronProfiles.templates.weekly' },
    { id: '0 0 1 * *', labelKey: 'cronProfiles.templates.monthly' },
    { id: '*/5 * * * *', labelKey: 'cronProfiles.templates.every5Minutes' },
    { id: '*/15 * * * *', labelKey: 'cronProfiles.templates.every15Minutes' },
    { id: '*/30 * * * *', labelKey: 'cronProfiles.templates.every30Minutes' },
];

export default function CronProfileForm() {
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id && id !== 'new');
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditing);

    const [selectedTemplate, setSelectedTemplate] = useState('custom');

    const { register, handleSubmit, control, reset, setValue, watch, formState: { errors } } = useForm<CronProfileCreatePayload>({
        defaultValues: {
            name: '',
            schedule: '* * * * *',
            enabled: true,
        }
    });

    // Individual schedule parts state
    const [scheduleParts, setScheduleParts] = useState({
        minute: '*',
        hour: '*',
        dayOfMonth: '*',
        month: '*',
        dayOfWeek: '*',
    });

    const currentSchedule = watch('schedule');

    // Generate options helpers
    const minuteOptions = useMemo(() => [
        {
            label: t('cronProfiles.presets.quickTemplate'),
            options: [
                { value: '*', label: t('cronProfiles.presets.minutes.everyMinute') },
                { value: '*/2', label: t('cronProfiles.presets.minutes.every2') },
                { value: '*/5', label: t('cronProfiles.presets.minutes.every5') },
                { value: '*/10', label: t('cronProfiles.presets.minutes.every10') },
                { value: '*/15', label: t('cronProfiles.presets.minutes.every15') },
                { value: '*/30', label: t('cronProfiles.presets.minutes.every30') },
            ]
        },
        {
            label: t('cronProfiles.presets.headers.minutes'),
            options: Array.from({ length: 60 }, (_, i) => ({
                value: i.toString(),
                label: `:${i.toString().padStart(2, '0')} (${i})`
            }))
        }
    ], [t]);

    const hourOptions = useMemo(() => [
        {
            label: t('cronProfiles.presets.quickTemplate'),
            options: [
                { value: '*', label: t('cronProfiles.presets.hours.everyHour') },
                { value: '*/2', label: t('cronProfiles.presets.hours.every2') },
                { value: '*/4', label: t('cronProfiles.presets.hours.every4') },
                { value: '*/6', label: t('cronProfiles.presets.hours.every6') },
                { value: '*/12', label: t('cronProfiles.presets.hours.every12') },
                { value: '0', label: t('cronProfiles.presets.hours.midnight') },
                { value: '12', label: t('cronProfiles.presets.hours.noon') },
                { value: '9-17', label: t('cronProfiles.presets.hours.business') },
            ]
        },
        {
            label: t('cronProfiles.presets.headers.hours'),
            options: Array.from({ length: 24 }, (_, i) => ({
                value: i.toString(),
                label: `${i.toString().padStart(2, '0')}:00 (${i})`
            }))
        }
    ], [t]);

    const dayOptions = useMemo(() => [
        {
            label: t('cronProfiles.presets.quickTemplate'),
            options: [
                { value: '*', label: t('cronProfiles.presets.days.everyDay') },
                { value: '*/2', label: t('cronProfiles.presets.days.everyOther') },
                { value: '1,15', label: t('cronProfiles.presets.days.1st15th') },
            ]
        },
        {
            label: t('cronProfiles.presets.headers.days'),
            options: Array.from({ length: 31 }, (_, i) => ({
                value: (i + 1).toString(),
                label: `${i + 1}`
            }))
        }
    ], [t]);

    const monthOptions = useMemo(() => [
        {
            label: t('cronProfiles.presets.quickTemplate'),
            options: [
                { value: '*', label: t('cronProfiles.presets.months.everyMonth') },
                { value: '*/3', label: t('cronProfiles.presets.months.quarterly') },
                { value: '1,7', label: t('cronProfiles.presets.months.janJul') },
            ]
        },
        {
            label: t('cronProfiles.presets.headers.months'),
            options: Array.from({ length: 12 }, (_, i) => ({
                value: (i + 1).toString(),
                label: `${i + 1}` // Could map to month names if needed, keeping simple for now
            }))
        }
    ], [t]);

    const weekdayOptions = useMemo(() => [
        {
            label: t('cronProfiles.presets.quickTemplate'),
            options: [
                { value: '*', label: t('cronProfiles.presets.weekdays.everyDay') },
                { value: '1-5', label: t('cronProfiles.presets.weekdays.monFri') },
                { value: '0,6', label: t('cronProfiles.presets.weekdays.weekend') },
                { value: '0', label: t('cronProfiles.presets.weekdays.sunday') },
                { value: '1', label: t('cronProfiles.presets.weekdays.monday') },
            ]
        },
        {
            label: t('cronProfiles.presets.headers.weekdays'),
            options: Array.from({ length: 7 }, (_, i) => ({
                value: i.toString(),
                label: `${i} (${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]})`
            }))
        }
    ], [t]);

    // Update parts when schedule changes (e.g., manual edit or initial load)
    useEffect(() => {
        if (currentSchedule) {
            const parts = currentSchedule.split(' ');
            if (parts.length === 5) {
                setScheduleParts({
                    minute: parts[0],
                    hour: parts[1],
                    dayOfMonth: parts[2],
                    month: parts[3],
                    dayOfWeek: parts[4],
                });

                // Check if matches a template
                const matchingTemplate = CRON_TEMPLATES.find(t => t.id === currentSchedule);
                if (matchingTemplate) {
                    setSelectedTemplate(matchingTemplate.id);
                } else {
                    setSelectedTemplate('custom');
                }
            }
        }
    }, [currentSchedule]);

    // Update schedule string when parts change
    const updateScheduleFromParts = (newParts: typeof scheduleParts) => {
        const newSchedule = `${newParts.minute} ${newParts.hour} ${newParts.dayOfMonth} ${newParts.month} ${newParts.dayOfWeek}`;
        setValue('schedule', newSchedule, { shouldValidate: true });
        setScheduleParts(newParts);
    };

    const handlePartChange = (part: keyof typeof scheduleParts, value: string) => {
        const newParts = { ...scheduleParts, [part]: value };
        updateScheduleFromParts(newParts);
    };

    const handleTemplateChange = (templateId: string) => {
        setSelectedTemplate(templateId);
        if (templateId !== 'custom') {
            setValue('schedule', templateId, { shouldValidate: true });
        }
    };

    useEffect(() => {
        if (isEditing && id) {
            const fetchProfile = async () => {
                try {
                    const profile = await getCronProfile(id);
                    reset({
                        name: profile.name,
                        schedule: profile.schedule,
                        enabled: profile.enabled,
                    });
                } catch (error) {
                    showError(error.message || t('cronProfiles.messages.fetchFailed'));
                    navigate('/admin/maintenance/cron-profiles');
                } finally {
                    setFetching(false);
                }
            };
            fetchProfile();
        }
    }, [isEditing, id, reset, showError, navigate, t]);

    const onSubmit = async (data: CronProfileCreatePayload, action?: 'save' | 'save-and-close' | 'save-and-new') => {
        setLoading(true);
        try {
            if (isEditing && id) {
                await updateCronProfile(id, data);
                showSuccess(t('cronProfiles.messages.updated'));
            } else {
                await createCronProfile(data);
                showSuccess(t('cronProfiles.messages.created'));
            }

            // Handle navigation based on save action
            if (action === 'save-and-close') {
                navigate('/admin/maintenance/cron-profiles');
            } else if (action === 'save-and-new') {
                // Determine if we are already on new page
                if (isEditing) {
                    navigate('/admin/maintenance/cron-profiles/new');
                } else {
                    // Reset form for new entry
                    reset({
                        name: '',
                        schedule: '* * * * *',
                        enabled: true,
                    });
                    setScheduleParts({
                        minute: '*',
                        hour: '*',
                        dayOfMonth: '*',
                        month: '*',
                        dayOfWeek: '*',
                    });
                    setSelectedTemplate('custom');
                }
            }
            // 'save' action stays on the page

        } catch (error) {
            showError(error.message || t('cronProfiles.messages.saveFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSaveWithAction = async (action: 'save' | 'save-and-close' | 'save-and-new') => {
        await handleSubmit((data) => onSubmit(data, action))();
    };

    if (fetching) {
        return <div className="p-8 text-center bg-background text-foreground">{t('common.loading', 'Loading...')}</div>;
    }

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                title={isEditing ? t('cronProfiles.editTitle') : t('cronProfiles.addTitle')}
                icon={<Clock className="h-5 w-5 text-primary" />}
                backButtonText={t('common.list', 'List')}
                onBack={() => navigate('/admin/maintenance/cron-profiles')}
            />

            <div className="mt-8">
                <form id="cron-profile-form" onSubmit={handleSubmit((data) => onSubmit(data, 'save-and-close'))} className="space-y-6">
                    <FormBox title={t('cronProfiles.generalInfo')} icon={<Info className="w-5 h-5" />}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                            {/* Name */}
                            <Label htmlFor="name" required className="text-left">
                                {t('cronProfiles.name')}
                            </Label>
                            <Input
                                id="name"
                                {...register('name', { required: t('common.requiredField', 'Field is required') })}
                                placeholder="e.g., Daily Cleanup"
                                className={errors.name ? 'border-red-500' : ''}
                            />

                            {/* Template */}
                            <Label htmlFor="template" className="text-left">{t('cronProfiles.template')}</Label>
                            <Select
                                options={CRON_TEMPLATES.map(t_item => ({ value: t_item.id, label: t(t_item.labelKey) }))}
                                value={selectedTemplate}
                                onValueChange={(value) => handleTemplateChange(value)}
                            />

                            {/* Enabled (Row 2) */}
                            <div className="col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 items-center">
                                <Label htmlFor="enabled" className="text-left">{t('cronProfiles.enabled')}</Label>
                                <div className="col-span-3">
                                    <Controller
                                        name="enabled"
                                        control={control}
                                        render={({ field }) => (
                                            <Toggle
                                                value={field.value ?? true}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            {errors.name && <p className="col-start-2 text-xs text-red-500">{errors.name.message}</p>}

                        </div>
                    </FormBox>

                    <FormBox title={t('cronProfiles.scheduleSettings')} icon={<Calendar className="w-5 h-5" />}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">

                            {/* Full Schedule */}
                            <Label htmlFor="schedule" className="text-left">
                                {t('cronProfiles.cronExpression')}
                            </Label>
                            <div className="col-span-2">
                                <Input
                                    id="schedule"
                                    {...register('schedule', {
                                        required: t('common.requiredField', 'Field is required'),
                                        pattern: {
                                            value: /^(\*|([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])|\*\/([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])) (\*|([0-9]|1[0-9]|2[0-3])|\*\/([0-9]|1[0-9]|2[0-3])) (\*|([1-9]|1[0-9]|2[0-9]|3[0-1])|\*\/([1-9]|1[0-9]|2[0-9]|3[0-1])) (\*|([1-9]|1[0-2])|\*\/([1-9]|1[0-2])) (\*|([0-6])|\*\/([0-6]))$/,
                                            message: t('common.invalidFormat', 'Invalid format')
                                        }
                                    })}
                                    placeholder="* * * * *"
                                    className={`font-mono ${errors.schedule ? 'border-red-500' : ''}`}
                                />
                            </div>
                            <div className="text-xs text-muted-foreground">{t('cronProfiles.cronFormat')}</div>

                            {/* Minute */}
                            <Label className="text-left">{t('cronProfiles.minute')}</Label>
                            <div className="col-span-2">
                                <Input
                                    value={scheduleParts.minute}
                                    onChange={(e) => handlePartChange('minute', e.target.value)}
                                    className="font-mono"
                                />
                            </div>
                            <Select
                                options={minuteOptions}
                                value={scheduleParts.minute}
                                onValueChange={(value) => handlePartChange('minute', value)}
                            />

                            {/* Hour */}
                            <Label className="text-left">{t('cronProfiles.hour')}</Label>
                            <div className="col-span-2">
                                <Input
                                    value={scheduleParts.hour}
                                    onChange={(e) => handlePartChange('hour', e.target.value)}
                                    className="font-mono"
                                />
                            </div>
                            <Select
                                options={hourOptions}
                                value={scheduleParts.hour}
                                onValueChange={(value) => handlePartChange('hour', value)}
                            />

                            {/* Day of Month */}
                            <Label className="text-left">{t('cronProfiles.dayOfMonth')}</Label>
                            <div className="col-span-2">
                                <Input
                                    value={scheduleParts.dayOfMonth}
                                    onChange={(e) => handlePartChange('dayOfMonth', e.target.value)}
                                    className="font-mono"
                                />
                            </div>
                            <Select
                                options={dayOptions}
                                value={scheduleParts.dayOfMonth}
                                onValueChange={(value) => handlePartChange('dayOfMonth', value)}
                            />

                            {/* Month */}
                            <Label className="text-left">{t('cronProfiles.month')}</Label>
                            <div className="col-span-2">
                                <Input
                                    value={scheduleParts.month}
                                    onChange={(e) => handlePartChange('month', e.target.value)}
                                    className="font-mono"
                                />
                            </div>
                            <Select
                                options={monthOptions}
                                value={scheduleParts.month}
                                onValueChange={(value) => handlePartChange('month', value)}
                            />

                            {/* Day of Week */}
                            <Label className="text-left">{t('cronProfiles.dayOfWeek')}</Label>
                            <div className="col-span-2">
                                <Input
                                    value={scheduleParts.dayOfWeek}
                                    onChange={(e) => handlePartChange('dayOfWeek', e.target.value)}
                                    className="font-mono"
                                />
                            </div>
                            <Select
                                options={weekdayOptions}
                                value={scheduleParts.dayOfWeek}
                                onValueChange={(value) => handlePartChange('dayOfWeek', value)}
                            />

                            {errors.schedule && <p className="col-start-2 text-xs text-red-500">{errors.schedule.message}</p>}
                        </div>
                    </FormBox>
                </form>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={() => reset()}
                isEditMode={isEditing}
                isSaving={loading}
                cancelText={t('common.cancel', 'Cancel')}
                showSave={true}
                showCancel={true}
                useSaveDropdown={true}
            />
        </div>
    );
}
